/**
 * Route tree (TanStack Router, code-based).
 *
 * `/login` and `/docs` are public; everything under the application layout requires an
 * authenticated member and renders inside `AppShell`. Filters live in typed
 * search params so lists are shareable by URL.
 */
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  notFound,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { type AuthContextValue, useAuth } from "@/app/auth";
import { AppShell } from "@/app/shell/AppShell";
import { AdminLayout } from "@/features/admin/AdminLayout";
import { MembersPage } from "@/features/admin/identity/MembersPage";
import { RolesPage } from "@/features/admin/identity/RolesPage";
import { SsoPage } from "@/features/admin/identity/SsoPage";
import { AuditLogPage, TeamsPage } from "@/features/admin/identity/TeamsAuditPages";
import { NotificationRulesPage } from "@/features/admin/integrations/NotificationRulesPage";
import { WebhooksPage } from "@/features/admin/integrations/WebhooksPage";
import { JobTemplateEditorPage } from "@/features/admin/maintenance/JobTemplateEditorPage";
import { JobTemplatesPage } from "@/features/admin/maintenance/JobTemplatesPage";
import { PlansAdminPage } from "@/features/admin/maintenance/PlansAdminPage";
import { FaultAutomationPage } from "@/features/admin/maintenance/FaultAutomationPage";
import { AssetTypesPage } from "@/features/admin/reference/AssetTypesPage";
import { AttributesPage } from "@/features/admin/reference/AttributesPage";
import { PartiesPage } from "@/features/admin/reference/PartiesPage";
import { SitesPage } from "@/features/admin/reference/SitesPage";
import { AssetDetailPage } from "@/features/assets/AssetDetailPage";
import { AssetsPage } from "@/features/assets/AssetsPage";
import { FleetMapPage } from "@/features/assets/FleetMapPage";
import { AcceptInvitePage } from "@/features/auth/AcceptInvitePage";
import { AccountPage } from "@/features/auth/AccountPage";
import { LoginPage } from "@/features/auth/LoginPage";
import {
  ResetPasswordConfirmPage,
  ResetPasswordRequestPage,
} from "@/features/auth/ResetPasswordPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { DOC_GUIDES } from "@/features/docs/catalog";
import { InventoryPage } from "@/features/inventory/InventoryPage";
import { ItemDetailPage } from "@/features/inventory/ItemDetailPage";
import { CalendarPage } from "@/features/maintenance/CalendarPage";
import {
  InternalFinancePage,
  internalFinanceSearchSchema,
} from "@/features/billing/InternalFinancePage";
import { ReportsPage, reportsSearchSchema } from "@/features/reports/ReportsPage";
import { CostCentersPage } from "@/features/admin/reference/CostCentersPage";
import { MaintenancePage } from "@/features/maintenance/MaintenancePage";
import { NotificationsPage } from "@/features/notifications/NotificationsPage";
import { RequestDetailPage } from "@/features/requests/RequestDetailPage";
import { RequestsPage } from "@/features/requests/RequestsPage";
import { WorkOrderDetailPage } from "@/features/workorders/WorkOrderDetailPage";
import { WorkOrdersPage } from "@/features/workorders/WorkOrdersPage";
import { LoadingState } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";

/** Router context injected from `App`. */
export interface RouterContext {
  auth: AuthContextValue;
}

/** Keep the public handbook readable while other routes wait for the auth probe. */
function RootComponent(): ReactElement {
  const { status } = useAuth();
  const isDocs = useRouterState({
    select: (state) =>
      state.location.pathname === "/docs" || state.location.pathname.startsWith("/docs/"),
  });
  if (status === "booting" && !isDocs) {
    return <LoadingState className="min-h-screen" />;
  }
  return <Outlet />;
}

/** 404 page. */
function NotFound(): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
      <p className="text-lg">{t("app.notFound")}</p>
      <Button asChild variant="outline">
        <a href="/">{t("app.backHome")}</a>
      </Button>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

/** Public login route; bounces authenticated users home. */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: function LoginRoute(): ReactElement {
    const { status } = useAuth();
    return status === "authenticated" ? <Navigate to="/" /> : <LoginPage />;
  },
});

const resetPasswordRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/reset-password",
  component: ResetPasswordRequestPage,
});

const resetPasswordConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/reset-password/$key",
  component: ResetPasswordConfirmPage,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/accept-invite",
  validateSearch: z.object({
    token: z.string().optional().catch(undefined),
    tenant: z.string().optional().catch(undefined),
  }),
  component: AcceptInvitePage,
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  validateSearch: z.object({ q: z.string().max(200).optional().catch(undefined) }),
  component: lazyRouteComponent(() => import("@/features/docs/DocsPortal"), "DocsLayout"),
  notFoundComponent: lazyRouteComponent(() => import("@/features/docs/DocsPortal"), "DocsNotFound"),
});
const docsIndexRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "/",
  component: lazyRouteComponent(() => import("@/features/docs/DocsPortal"), "DocsHome"),
});
const docsGuideRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "/$guideId",
  beforeLoad: ({ params }) => {
    if (!DOC_GUIDES.some((guide) => guide.id === params.guideId)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router not-found signal
      throw notFound();
    }
  },
  component: lazyRouteComponent(() => import("@/features/docs/DocsPortal"), "DocsGuide"),
  notFoundComponent: lazyRouteComponent(() => import("@/features/docs/DocsPortal"), "DocsNotFound"),
});
const docsCatchAllRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "$",
  component: lazyRouteComponent(() => import("@/features/docs/DocsPortal"), "DocsNotFound"),
});

/** Authenticated layout; redirects anonymous users to `/login`. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: ({ context }) => {
    if (context.auth.status === "anonymous") {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router idiom
      throw redirect({ to: "/login" });
    }
  },
  component: AppShell,
});

/** Common list filter params. */
export const listSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  status: z.string().optional().catch(undefined),
  open_only: z.boolean().optional().catch(undefined),
  linked: z.boolean().optional().catch(undefined),
  mine: z.boolean().optional().catch(undefined),
  priority: z.string().optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  severity: z.string().optional().catch(undefined),
  vessels_only: z.boolean().optional().catch(undefined),
  ownership: z.enum(["owned", "external"]).optional().catch(undefined),
  asset_id: z.string().optional().catch(undefined),
  unread: z.boolean().optional().catch(undefined),
  tab: z.string().optional().catch(undefined),
  // Facets shared by several lists.
  site_id: z.string().optional().catch(undefined),
  location_id: z.string().optional().catch(undefined),
  item_id: z.string().optional().catch(undefined),
  operations_item_id: z.string().optional().catch(undefined),
  movements_item_id: z.string().optional().catch(undefined),
  stock_q: z.string().optional().catch(undefined),
  operations_q: z.string().optional().catch(undefined),
  movements_q: z.string().optional().catch(undefined),
  operation_kind: z.string().optional().catch(undefined),
  operation_status: z.string().optional().catch(undefined),
  needs_review: z.boolean().optional().catch(undefined),
  movement_type: z.string().optional().catch(undefined),
  movement_from: z.string().optional().catch(undefined),
  movement_to: z.string().optional().catch(undefined),
  operation_id: z.string().optional().catch(undefined),
  entity_type: z.string().optional().catch(undefined),
  entity_id: z.string().optional().catch(undefined),
  tags: z.string().optional().catch(undefined),
  type_key: z.string().optional().catch(undefined),
  criticality: z.string().optional().catch(undefined),
  kind: z.string().optional().catch(undefined),
  category_id: z.string().optional().catch(undefined),
  plan_id: z.string().optional().catch(undefined),
  overdue: z.boolean().optional().catch(undefined),
  due_before: z.string().optional().catch(undefined),
  due_after: z.string().optional().catch(undefined),
  completed_after: z.string().optional().catch(undefined),
  completed_before: z.string().optional().catch(undefined),
  days: z.coerce.number().int().optional().catch(undefined),
  below_min: z.boolean().optional().catch(undefined),
  include_inactive: z.boolean().optional().catch(undefined),
  // Table state: 1-based page, page size, server ordering ("-due_at").
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  size: z.coerce.number().int().min(10).max(200).optional().catch(undefined),
  sort: z.string().optional().catch(undefined),
});

/** Parsed list search params. */
export type ListSearch = z.infer<typeof listSearchSchema>;

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: DashboardPage,
  validateSearch: listSearchSchema,
});
const mapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/map",
  validateSearch: listSearchSchema,
  component: FleetMapPage,
});
const assetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/assets",
  validateSearch: listSearchSchema,
  component: AssetsPage,
});
const assetDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/assets/$assetId",
  validateSearch: z.object({
    tab: z.enum(["components", "meters", "history"]).optional().catch(undefined),
  }),
  component: AssetDetailPage,
});
const workOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/work-orders",
  validateSearch: listSearchSchema,
  component: WorkOrdersPage,
});
const workOrderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/work-orders/$woId",
  component: WorkOrderDetailPage,
});
const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/requests",
  validateSearch: listSearchSchema,
  component: RequestsPage,
});
const requestDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/requests/$requestId",
  component: RequestDetailPage,
});
const maintenanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/maintenance",
  validateSearch: listSearchSchema,
  component: MaintenancePage,
});
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calendar",
  validateSearch: listSearchSchema,
  component: CalendarPage,
});
const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory",
  validateSearch: listSearchSchema,
  component: InventoryPage,
});
const warehouseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse",
  validateSearch: listSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/inventory/WarehousePage"),
    "WarehousePage",
  ),
});
const warehouseOperationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/operations/$operationId",
  validateSearch: listSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/inventory/WarehousePage"),
    "WarehouseOperationPage",
  ),
});
const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents",
  validateSearch: listSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/documents/DocumentsPage"),
    "DocumentsPage",
  ),
});
const documentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/$documentId",
  validateSearch: (search: Record<string, unknown>): { version_id?: string } => ({
    version_id: typeof search.version_id === "string" ? search.version_id : undefined,
  }),
  component: lazyRouteComponent(
    () => import("@/features/documents/DocumentDetailPage"),
    "DocumentDetailPage",
  ),
});
const itemDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/$itemId",
  validateSearch: listSearchSchema,
  component: ItemDetailPage,
});
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notifications",
  validateSearch: listSearchSchema,
  component: NotificationsPage,
});
const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/account",
  component: AccountPage,
});
const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing",
  validateSearch: internalFinanceSearchSchema,
  component: InternalFinancePage,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  validateSearch: reportsSearchSchema,
  component: ReportsPage,
});

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin",
  component: AdminLayout,
});
const documentSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/document-settings",
  component: lazyRouteComponent(
    () => import("@/features/documents/DocumentsPage"),
    "DocumentSettingsPage",
  ),
});

const adminMembersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/members",
  validateSearch: listSearchSchema,
  component: MembersPage,
});
const adminRolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/roles",
  validateSearch: listSearchSchema,
  component: RolesPage,
});
const adminTeamsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/teams",
  validateSearch: listSearchSchema,
  component: TeamsPage,
});
const adminSsoRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/sso",
  component: SsoPage,
});
const adminSitesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/sites",
  validateSearch: listSearchSchema,
  component: SitesPage,
});
const adminPartiesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/parties",
  validateSearch: listSearchSchema,
  component: PartiesPage,
});
const adminCostCentersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/cost-centers",
  validateSearch: listSearchSchema,
  component: CostCentersPage,
});
const adminAssetTypesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/asset-types",
  validateSearch: listSearchSchema,
  component: AssetTypesPage,
});
const adminAttributesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/attributes",
  validateSearch: listSearchSchema,
  component: AttributesPage,
});
const adminJobTemplatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/job-templates",
  validateSearch: listSearchSchema,
  component: JobTemplatesPage,
});
const adminJobTemplateNewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/job-templates/new",
  component: JobTemplateEditorPage,
});
const adminJobTemplateEditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/job-templates/$templateId",
  component: JobTemplateEditorPage,
});
const adminPlansRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/plans",
  validateSearch: listSearchSchema,
  component: PlansAdminPage,
});
const adminRulesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/notification-rules",
  validateSearch: listSearchSchema,
  component: NotificationRulesPage,
});
const adminFaultAutomationRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/fault-automation",
  component: FaultAutomationPage,
});
const adminWebhooksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/webhooks",
  validateSearch: listSearchSchema,
  component: WebhooksPage,
});
const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/audit",
  validateSearch: listSearchSchema,
  component: AuditLogPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRequestRoute,
  resetPasswordConfirmRoute,
  acceptInviteRoute,
  docsRoute.addChildren([docsIndexRoute, docsGuideRoute, docsCatchAllRoute]),
  appRoute.addChildren([
    indexRoute,
    mapRoute,
    assetsRoute,
    assetDetailRoute,
    workOrdersRoute,
    workOrderDetailRoute,
    requestsRoute,
    requestDetailRoute,
    maintenanceRoute,
    calendarRoute,
    inventoryRoute,
    warehouseRoute,
    warehouseOperationRoute,
    documentsRoute,
    documentDetailRoute,
    billingRoute,
    reportsRoute,
    itemDetailRoute,
    notificationsRoute,
    accountRoute,
    adminRoute.addChildren([
      documentSettingsRoute,
      adminMembersRoute,
      adminRolesRoute,
      adminTeamsRoute,
      adminSsoRoute,
      adminSitesRoute,
      adminPartiesRoute,
      adminCostCentersRoute,
      adminAssetTypesRoute,
      adminAttributesRoute,
      adminJobTemplatesRoute,
      adminJobTemplateNewRoute,
      adminJobTemplateEditRoute,
      adminPlansRoute,
      adminFaultAutomationRoute,
      adminRulesRoute,
      adminWebhooksRoute,
      adminAuditRoute,
    ]),
  ]),
]);

/** Build the router; `auth` is provided via `RouterProvider context`. */
export function createAppRouter(): ReturnType<typeof createRouter<typeof routeTree>> {
  return createRouter({
    routeTree,
    context: { auth: undefined as unknown as AuthContextValue },
    defaultPreload: "intent",
    scrollRestoration: true,
  });
}

/** Router type registration for type-safe links. */
declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

export {
  assetDetailRoute,
  assetsRoute,
  inventoryRoute,
  warehouseRoute,
  warehouseOperationRoute,
  documentsRoute,
  documentSettingsRoute,
  documentDetailRoute,
  itemDetailRoute,
  maintenanceRoute,
  notificationsRoute,
  requestDetailRoute,
  requestsRoute,
  workOrderDetailRoute,
  workOrdersRoute,
};
